import {DEFAULT_HARVEST_PLANT_COST_TYPE, DEFAULT_OVERVIEW_TABLE_METRIC, DEFAULT_WEIGHT_UNIT} from 'appConstants';
import {
  type HarvestExplanationsEnum,
  type ActionSpeciesEnum,
  type DistributionRowQualityClassEnum,
  type DistributionRowWeightClassEnum,
  type HarvestDemandPutCertificatesEnum,
  type HarvestDemandPutSpeciesEnum,
  type HarvestPlantCostTypeEnum,
} from 'client';
import {type WeightUnits} from 'interfaces';
import {createContext, useContext, useState, type FC, useMemo} from 'react';
import {ContextEnum, type MetricsEnum} from '../components/OverviewTable/interfaces';

export interface IFilterContext {
  hideEmptySites?: boolean;
  hideEmptyCages?: boolean;
  concessionMetric?: 'biomass' | 'delta';
  metric?: MetricsEnum;
  visibleDate?: string;
  context?: ContextEnum;
  selectedPlans?: string[];
  hideDelta?: boolean;
  selectedRegions?: string[];
  shortTermViewSelectedSites?: string[];
  shortTermViewSelectedCages?: string[];
  shortTermViewSelectedSiteGroups?: string[];
  shortTermViewSelectedActions?: string[];
  shortTermViewSelectedPackagingCodes?: string[];
  overviewTableSelectedSites?: string[];
  overviewTableSelectedSiteGroups?: string[];
  overviewTableSelectedSpecies?: ActionSpeciesEnum[];
  overviewTableSelectedCertificates?: string[];
  longTermViewSelectedSites?: string[];
  longTermViewSelectedSiteGroups?: string[];
  longTermViewSelectedPeriod?: {startDate: string; endDate: string};
  shortTermViewSelectedSpecies?: string[];
  shortTermViewSelectedCertificates?: string[];
  overviewSelectedSites?: string[];
  overviewSelectedCertificates?: HarvestDemandPutCertificatesEnum[];
  siteOverviewSelectedSites?: string[];
  overviewSelectedHarvestWeightClasses?: DistributionRowWeightClassEnum[];
  overviewSelectedHarvestQualityClasses?: DistributionRowQualityClassEnum[];
  overviewSelectedPeriod?: {startDate: string; endDate: string};
  overviewSelectedSpecies?: HarvestDemandPutSpeciesEnum[];
  overviewSelectedHarvestPlants?: string[];
  overviewHarvestExplanations?: HarvestExplanationsEnum[];
  weightUnit?: WeightUnits;
  overviewTableColumns?: string;
  shortTermColumns?: string;
  shortTermViewSelectedFishGroupProperties?: string[];
  harvestPlantCostType?: HarvestPlantCostTypeEnum;
}

const locelStorageKey = 'filter_state';

const FilterTableContext = createContext<IFilterContext>({});
export const useFilterContext = () => useContext(FilterTableContext);

export const FilterContextProvider: FC<any> = ({children}) => {
  const initalState = JSON.parse(localStorage.getItem(locelStorageKey) ?? '{}');

  const [contextData, setContextData] = useState<IFilterContext>(initalStateHandler(initalState));

  const proxyHandler = {
    set(target: IFilterContext, prop: keyof IFilterContext, value: any) {
      const siteProp = prop.includes('SiteGroups') ? {[prop]: value, [prop.replace('Group', '')]: []} : {};

      const storageData = JSON.parse(localStorage.getItem(locelStorageKey) ?? '{}');
      const newState = {...storageData, [prop]: value, ...siteProp};

      localStorage.setItem(locelStorageKey, JSON.stringify(newState));
      setContextData(newState);

      return true;
    },
    get(target: IFilterContext, prop: keyof IFilterContext) {
      const state = initalStateHandler(JSON.parse(localStorage.getItem(locelStorageKey) ?? '{}'));
      return state[prop];
    },
  };

  const value = useMemo(() => new Proxy(contextData, proxyHandler), [JSON.stringify(contextData)]);

  return <FilterTableContext.Provider value={value}>{children}</FilterTableContext.Provider>;
};

const initalStateHandler = (initalState: IFilterContext): IFilterContext => {
  return {
    context: ContextEnum.growthMortalityLice,
    ...initalState,
    concessionMetric: initalState.concessionMetric ?? 'delta',
    metric: initalState.metric ?? DEFAULT_OVERVIEW_TABLE_METRIC,
    selectedPlans: (initalState.selectedPlans as string[]) || [],
    overviewTableSelectedSiteGroups: (initalState.overviewTableSelectedSiteGroups as string[]) || [],
    weightUnit: initalState.weightUnit ?? DEFAULT_WEIGHT_UNIT,
    harvestPlantCostType: initalState.harvestPlantCostType ?? DEFAULT_HARVEST_PLANT_COST_TYPE,
    overviewTableColumns: initalState.overviewTableColumns,
    shortTermColumns: initalState.shortTermColumns,
  };
};
